import { Head } from 'vite-react-ssg';
import { brand, type Locale } from '../config/brand';
import { pageRoutes, type PageId } from '../config/page-routes';

interface SeoProps {
  locale: Locale;
  pageId?: PageId;
  title?: string;
  description?: string;
}

/**
 * Per-route SEO + structured data.
 *
 * Emits:
 *  - canonical + hreflang alternates
 *  - Open Graph / Twitter cards
 *  - JSON-LD: LocalBusiness (always), WebSite with SearchAction,
 *    SiteNavigationElement (signals Google to show sitelinks)
 *  - BreadcrumbList when not on the home page
 */
export function Seo({ locale, pageId = 'home', title, description }: SeoProps) {
  const resolvedTitle = title ?? `${brand.name} · ${brand.tagline[locale]}`;
  const resolvedDescription = description ?? brand.description[locale];
  const canonical = `${brand.url}${pageRoutes[pageId][locale]}`;

  const localBusiness = {
    '@context': 'https://schema.org',
    '@type': ['LocalBusiness', 'SportsActivityLocation', 'HealthClub'],
    '@id': `${brand.url}/#organization`,
    name: brand.name,
    legalName: brand.legalName,
    taxID: brand.legalNif,
    description: brand.description[locale],
    url: brand.url,
    telephone: brand.contact.phone,
    email: brand.contact.email,
    image: `${brand.url}${brand.logo}`,
    logo: `${brand.url}${brand.logo}`,
    priceRange: '€€',
    currenciesAccepted: 'EUR',
    paymentAccepted: 'Cash, Credit Card',
    hasMap: brand.googleMapsUrl,
    areaServed: [
      { '@type': 'City', name: 'Santa Cruz de Tenerife' },
      { '@type': 'AdministrativeArea', name: 'Tenerife' },
    ],
    address: {
      '@type': 'PostalAddress',
      streetAddress: brand.address.street,
      addressLocality: brand.address.locality,
      addressRegion: brand.address.region,
      postalCode: brand.address.postalCode,
      addressCountry: brand.address.country,
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: brand.geo.latitude,
      longitude: brand.geo.longitude,
    },
    openingHoursSpecification: brand.openingHours.map((oh) => ({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: oh.days,
      opens: oh.opens,
      closes: oh.closes,
    })),
    sameAs: [brand.contact.facebook, brand.contact.instagram, brand.contact.youtube],
    founder: {
      '@type': 'Person',
      name: brand.legalName,
    },
    makesOffer: brand.services.map((s) => ({
      '@type': 'Offer',
      itemOffered: { '@type': 'Service', name: s[locale] },
    })),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: brand.aggregateRating.ratingValue,
      reviewCount: brand.aggregateRating.reviewCount,
      bestRating: 5,
      worstRating: 1,
    },
    inLanguage: brand.locales,
  };

  // WebSite schema — tells Google about the site and enables sitelinks search box
  const webSite = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${brand.url}/#website`,
    name: brand.name,
    url: brand.url,
    inLanguage: brand.locales,
    publisher: { '@id': `${brand.url}/#organization` },
  };

  // SiteNavigationElement — signals the nav structure for sitelinks
  const siteNav = {
    '@context': 'https://schema.org',
    '@type': 'SiteNavigationElement',
    name: Object.keys(pageRoutes),
    url: Object.values(pageRoutes).map((paths) => `${brand.url}${paths[locale]}`),
  };

  // BreadcrumbList for non-home pages
  const breadcrumb =
    pageId !== 'home'
      ? {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: brand.name,
              item: `${brand.url}${pageRoutes.home[locale]}`,
            },
            { '@type': 'ListItem', position: 2, name: resolvedTitle.split(' · ')[0] },
          ],
        }
      : null;

  const schemas = [localBusiness, webSite, siteNav, breadcrumb].filter(Boolean);

  return (
    <Head htmlAttributes={{ lang: locale }}>
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <link rel="canonical" href={canonical} />

      {brand.locales.map((l) => (
        <link key={l} rel="alternate" hrefLang={l} href={`${brand.url}${pageRoutes[pageId][l]}`} />
      ))}
      <link
        rel="alternate"
        hrefLang="x-default"
        href={`${brand.url}${pageRoutes[pageId][brand.defaultLocale]}`}
      />

      <meta property="og:type" content="website" />
      <meta property="og:locale" content={locale === 'es' ? 'es_ES' : 'en_GB'} />
      <meta property="og:site_name" content={brand.name} />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={`${brand.url}${brand.logo}`} />
      <meta property="og:image:width" content="1135" />
      <meta property="og:image:height" content="427" />
      <meta property="og:image:type" content="image/png" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={`${brand.url}${brand.logo}`} />

      <meta name="theme-color" content="#009739" />

      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Head>
  );
}
